import { DbStandardTypeList, type DbTypeConfigSource } from '../types.js'
import { DbStandardTypes } from '../types.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import { jsonProperty, jsonClass } from '@sumbricht/json-class-serializer'
import type { ValueTransformer } from 'typeorm'

@jsonClass('ServerDataStoreEntryDbField')
export class ServerDataStoreEntryDbField {
	@jsonProperty(String)
	columnTypeSource: DbTypeConfigSource = 'default'
	@jsonProperty(String)
	customColumnType?: keyof DbStandardTypes
	@jsonProperty(String)
	manualColumnType?: string
	// do not serialize this property
	defaultColumnType?: keyof DbStandardTypes

	@jsonProperty(Number)
	length: number | undefined = undefined
	@jsonProperty(Number)
	precision: number | undefined = undefined
	@jsonProperty(Number)
	scale: number | undefined = undefined

	@jsonProperty(Boolean)
	omit: boolean = false
	@jsonProperty(Boolean)
	autoGenerate: boolean = false
	@jsonProperty(Boolean)
	unique: boolean = false

	constructor(init?: Partial<ServerDataStoreEntryDbField>) {
		assignCommonProperties(this, init)
	}

	setDefaultForTsType(tsType: string) {
		if ((DbStandardTypeList as readonly string[]).includes(tsType)) {
			this.defaultColumnType = tsType as keyof DbStandardTypes
		} else if (
			tsType.includes('Record<') ||
			tsType.includes('Array<') ||
			tsType.includes('[]')
		) {
			this.defaultColumnType = 'json'
		}
	}

	getColumnType(): keyof DbStandardTypes | undefined {
		if (this.columnTypeSource == 'default') {
			if (this.defaultColumnType == 'number' && this.scale > 0) return 'double'
			return this.defaultColumnType
		}
		if (this.columnTypeSource == 'custom') {
			return this.customColumnType
		}
		return undefined
	}

	hasLength() {
		const columnType = this.getColumnType()
		return columnType == 'string' || columnType == 'vector'
	}

	isShowPrecision() {
		switch (this.columnTypeSource) {
			case 'default':
				return this.defaultColumnType == 'number'
			case 'custom':
				return (
					this.customColumnType == 'number' || this.customColumnType == 'double'
				)
		}
		return false
	}

	isShowScale() {
		switch (this.columnTypeSource) {
			case 'default':
				return this.defaultColumnType == 'number'
			case 'custom':
				return this.customColumnType == 'double'
		}
		return false
	}

	hasPrecision() {
		const columnType = this.getColumnType()
		return columnType == 'double'
	}

	hasScale() {
		const columnType = this.getColumnType()
		return columnType == 'double'
	}

	getLength(): number | undefined {
		return this.hasLength() ? (this.length ?? 50) : undefined
	}
	getPrecision(): number | undefined {
		return this.hasPrecision() ? (this.precision ?? 10) : undefined
	}
	getScale(): number | undefined {
		return this.hasScale() ? (this.scale ?? 2) : undefined
	}

	getValueTransformers(): ValueTransformer[] {
		const columnType = this.getColumnType()
		if (columnType == 'double') {
			return [
				{
					from(value: string) {
						return parseFloat(value)
					},
					to(value: number) {
						return value
					},
				},
			]
		}
		return []
	}
}
